<template>
  <ForumReactions
    :show-animation="isReactionAnimationsEnabled"
    :reactions="mergedReactions"
    :disabled="disabled"
    @select="onSelectReaction"
    @viewReaction="onViewReaction"
  />
</template>

<script>
import { ForumThreadModel } from '@/models/forum/forumThread'
import emojiToHtml from '@/utils/emojiToHtml'
import { ForumReactionModel } from '@/models/forum/forumReaction'
import ForumReactions from '@/components/pages/forum/ForumReactions'
import { ReactionsEnums } from '@/common/reactionsEnums'
import _ from 'lodash'

const debounceTime = 1000

export default {
  name: 'ForumThreadReactions',
  components: { ForumReactions },
  props: {
    thread: { type: ForumThreadModel, required: true },
    reactionEmojiList: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    isReactionAnimationsEnabled: { type: Boolean, default: true }
  },
  data() {
    return {
      debounceActive: false,
      localReactionEmojiList: {},
      updatingRequest: null,
      cachedReactions: null
    }
  },
  computed: {
    reactionsLength() {
      return this.thread?.reactions?.length
    },
    mergedReactions() {
      const currentReactions = {}
      this.thread.reactions.forEach((reaction) => {
        currentReactions[reaction.emoji] = reaction
      })

      const unknownReactions = this.reactionEmojiList.filter((emoji) => !currentReactions[emoji])
      const allReactions = [
        ...this.thread.reactions.map((thread) => ({
          ...thread,
          emojiHtml: emojiToHtml.span(thread.emoji)
        })),
        ...unknownReactions.map((emoji) => ({
          emoji,
          emojiHtml: emojiToHtml.span(emoji),
          count: 1,
          isOwnReaction: true
        }))
      ]

      return allReactions
        .map((reaction) => {
          let count
          if (!reaction.isOwnReaction && this.reactionEmojiList.find((emoji) => emoji === reaction.emoji)) {
            count = reaction.count + 1
          } else if (
            reaction.isOwnReaction &&
            !this.reactionEmojiList.find((emoji) => emoji === reaction.emoji)
          ) {
            count = reaction.count - 1
          } else {
            count = reaction.count
          }
          return new ForumReactionModel({
            ...reaction,
            count,
            isOwnReaction: this.reactionEmojiList.find((emoji) => emoji === reaction.emoji)
          })
        })
        .filter((reaction) => reaction.count !== 0)
        .sort((a, b) => {
          if (a.count !== b.count) {
            return b.count - a.count
          }
          if (a.isOwnReaction && !b.isOwnReaction) {
            return -1
          } else if (!a.isOwnReaction && b.isOwnReaction) {
            return 1
          } else {
            return 0
          }
        })
    }
  },
  watch: {
    thread: {
      handler() {
        if (this.debounceActive) {
          return
        }
        const localReactionEmojiList =
          this.thread?.reactions
            ?.filter((reaction) => reaction?.isOwnReaction)
            ?.map((reaction) => reaction?.emoji) || []
        if (!this.$_.isEqual(localReactionEmojiList, this.localReactionEmojiList)) {
          this.localReactionEmojiList = localReactionEmojiList
        }
      }
    },
    reactionEmojiList: {
      deep: true,
      handler() {
        if (this.debounceActive) {
          return
        }
        if (!this.$_.isEqual(this.reactionEmojiList, this.localReactionEmojiList)) {
          this.localReactionEmojiList = [...this.reactionEmojiList]
          this.sendReactions()
        }
      }
    },
    localReactionEmojiList: {
      deep: true,
      handler() {
        this.$emit('updateReactionEmojiList', [...this.localReactionEmojiList])
      }
    }
  },
  created() {
    this.initReactionEmojiList()
  },
  methods: {
    initReactionEmojiList() {
      const reactions = this.thread?.reactions
        ?.filter((reaction) => reaction?.isOwnReaction)
        .map((reaction) => reaction.emoji)
      this.localReactionEmojiList = reactions
      this.$emit('updateReactionEmojiList', [...this.localReactionEmojiList])
    },
    onSelectReaction(reaction) {
      const reactionIndex = this.localReactionEmojiList.indexOf(reaction.emoji)
      if (reactionIndex !== -1) {
        this.localReactionEmojiList.splice(reactionIndex, 1)
      } else {
        this.localReactionEmojiList.push(reaction.emoji)
      }

      this.sendReactions()
    },
    sendReactions() {
      this.debounceActive = true
      this.sendReactionsDebounced()
    },
    onViewReaction(reaction) {
      this.$api.forum.threads.viewReaction({
        chatId: this.thread.chatId,
        threadId: this.thread.id,
        emoji: reaction.emoji
      })
    },
    sendReactionsDebounced: _.debounce(function () {
      const isEmojiAnimated = (emoji) => {
        return !!Object.keys(ReactionsEnums).find((reactionKey) => ReactionsEnums[reactionKey] === emoji)
      }
      this.debounceActive = false
      const updatingRequest = this.$api.forum.threads.updateReactions({
        chatId: this.thread.chatId,
        threadId: this.thread.id,
        emojiList: this.localReactionEmojiList.map((emoji) => ({
          emoji,
          isAnimated: isEmojiAnimated(emoji)
        }))
      })
      this.updatingRequest = updatingRequest
      const currentReactions = this.thread.reactions.map((reaction) => new ForumReactionModel(reaction))
      updatingRequest
        .then((thread) => {
          if (this.updatingRequest !== updatingRequest) {
            return
          }
          const reactions = thread?.reactions?.map((reaction) =>
            ForumReactionModel.parseFromApi({ reaction })
          )
          if (this.debounceActive) {
            this.cachedReactions = reactions
          }
          this.cachedReactions = null
          this.$emit('updateReactions', { reactions })
        })
        .catch((e) => {
          console.log(e)
          if (this.cachedReactions) {
            this.$emit('updateReactions', { reactions: this.cachedReactions })
            this.cachedReactions = null
          } else {
            this.$emit('updateReactions', { reactions: currentReactions })
          }
        })
    }, debounceTime)
  }
}
</script>

<style lang="scss" scoped>
.ForumThreadReactions {
}
</style>
